<script setup lang="ts">
import { ref, watch, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@/firebase'
import { useAuthStore } from '@/stores/auth'
import { usePersonnelStore } from '@/stores/personnel'
import { useOpsStore } from '@/stores/ops'
import { OverheadRole } from '@/types'
import CheckInModal from '@/components/personnel/CheckInModal.vue'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const personnelStore = usePersonnelStore()
const opsStore = useOpsStore()

const incidentId = route.params.id as string
const token = route.query.token as string | undefined
const inviteId = route.query.invite as string | undefined
const roleParam = route.query.role as string | undefined
// Only honor a role param that matches a real overhead role.
const prefillRole = (Object.values(OverheadRole) as string[]).includes(roleParam ?? '')
  ? (roleParam as OverheadRole)
  : null
const showCheckIn = ref(false)
const evaluated = ref(false)
const tokenError = ref<string | null>(null)

function redirectToOverview() {
  router.replace({ name: 'incident-overview', params: { id: incidentId } })
}

async function verifyToken(): Promise<boolean> {
  if (!token) {
    tokenError.value = 'No join token provided.'
    return false
  }
  try {
    const idToken = await authStore.currentUser?.getIdToken()
    const res = await fetch(`/api/incidents/${incidentId}/personnel/verify-join-token`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    })
    const json = await res.json()
    if (!res.ok || json.error) {
      tokenError.value = json.error?.message ?? 'Invalid or expired join link.'
      return false
    }
    return true
  } catch {
    tokenError.value = 'Could not verify join link. Check your connection.'
    return false
  }
}

async function evaluate() {
  if (evaluated.value) return
  evaluated.value = true

  const alreadyIn = personnelStore.roster.some(
    (p) => p.user_id === authStore.currentUser?.uid && p.time_out === null,
  )

  if (alreadyIn) {
    redirectToOverview()
    return
  }

  const valid = await verifyToken()
  if (!valid) return

  showCheckIn.value = true
}

// Wait for Firebase auth to resolve, then load ops → personnel → evaluate
let authChecked = false
watch(
  () => authStore.authReady,
  (ready) => {
    if (!ready || authChecked) return
    authChecked = true

    if (!authStore.isAuthenticated) {
      router.replace({ name: 'login', query: { redirect: route.fullPath } })
      return
    }

    opsStore.subscribeToOps(incidentId)

    // Once the selected OP is known, subscribe to that OP's personnel then evaluate
    let rosterChecked = false
    watch(
      () => opsStore.selectedOpId,
      (opId) => {
        if (!opId) return
        personnelStore.subscribeToPersonnel(incidentId, opId)

        // Wait for first personnel snapshot, then decide (only once)
        watch(
          () => personnelStore.loading,
          (loading) => {
            if (loading || rosterChecked) return
            rosterChecked = true
            evaluate()
          },
          { immediate: true },
        )
      },
      { immediate: true },
    )
  },
  { immediate: true },
)

// If this user is now checked in and arrived via an email invite, mark the
// invite claimed (best-effort — they are an incident member by now).
async function maybeClaimInvite() {
  if (!inviteId) return
  const uid = authStore.currentUser?.uid
  const checkedIn = personnelStore.roster.some((p) => p.user_id === uid && p.time_out === null)
  if (!checkedIn) return
  try {
    await updateDoc(doc(db, 'incidents', incidentId, 'invites', inviteId), {
      status: 'claimed',
      claimed_by_uid: uid ?? null,
      claimed_at: serverTimestamp(),
    })
  } catch {
    // best-effort; overhead can still revoke a stale invite manually
  }
}

// After modal closes (success or cancel), return to the incident
watch(showCheckIn, async (val) => {
  if (evaluated.value && !val) {
    await maybeClaimInvite()
    redirectToOverview()
  }
})

onUnmounted(() => {
  personnelStore.teardown()
  opsStore.teardown()
})
</script>

<template>
  <CheckInModal v-model:visible="showCheckIn" :incident-id="incidentId" :prefill-overhead-role="prefillRole" />
  <div class="min-h-screen flex flex-col">
    <header class="bg-surface px-6 py-4 border-b border-outline-variant/15">
      <span class="text-on-surface text-lg font-semibold tracking-wide uppercase">First on Scene</span>
    </header>
    <div class="flex-1 flex items-center justify-center">
      <div v-if="tokenError" class="flex flex-col items-center gap-3 text-center px-6 max-w-sm">
        <span class="material-symbols-outlined text-error text-5xl">cancel</span>
        <p class="text-on-surface font-medium">Invalid Join Link</p>
        <p class="text-on-surface-variant text-sm">{{ tokenError }}</p>
        <p class="text-outline text-xs">Ask your Incident Commander to regenerate the QR code.</p>
      </div>
      <div v-else class="flex flex-col items-center gap-3 text-outline">
        <div class="w-8 h-8 border-2 border-outline/30 border-t-outline animate-spin" />
        <span class="text-sm">Loading…</span>
      </div>
    </div>
  </div>
</template>
